import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Col,
  Input,
  InputNumber,
  Modal,
  Radio,
  Row,
  Select,
  Switch,
} from 'antd';
import { CalculatorOutlined } from '@ant-design/icons';
import CapacityCalculatorModal from './CapacityCalculatorModal';
import { MODAL_Z_INDEX } from './modalLayer';

/** Same palette as the dashboard editor, kept one click away. */
const PRESET_COLORS = ['#353A0A', '#A1E430', '#2F7ED8', '#E0A800', '#D4380D', '#7F5AF0'];

const EMPTY_FORM = {
  title: '',
  inputType: '',
  movementType: 'straight',
  approachBearing: 0,
  lanes: 1,
  laneWidth: 3.25,
  flow: null,
  verdeMin: null,
  color: '#353A0A',
  capSatResult: null,
  isManual: false,
  capacityInputs: null,
};

export default function ManeuverModal({
  open,
  maneuver,
  defaultTitle,
  geometry,
  onCancel,
  onSave,
}) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [capacityOpen, setCapacityOpen] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm({
      ...EMPTY_FORM,
      title: maneuver?.title || defaultTitle || '',
      inputType: maneuver?.inputType || '',
      movementType: maneuver?.movementType || 'straight',
      approachBearing: Number(maneuver?.approachBearing || 0),
      lanes: maneuver?.lanes ?? 1,
      laneWidth: maneuver?.laneWidth ?? 3.25,
      flow: maneuver?.flussi?.[0]?.value ?? null,
      verdeMin: maneuver?.verdeMin ?? null,
      color: maneuver?.color || '#353A0A',
      capSatResult: maneuver?.capSatResult ?? null,
      isManual: Boolean(maneuver?.manual ?? maneuver?.isManual),
      capacityInputs: maneuver?.capacityInputs || null,
    });
    setError('');
  }, [defaultTitle, maneuver, open]);

  const update = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
    setError('');
  };

  const validate = () => {
    if (!form.inputType) return 'Seleziona Input veicolare o Input pedonale.';
    if (form.inputType === 'veicolare') {
      if (!(Number(form.lanes) >= 1)) return 'Inserisci almeno una corsia.';
      if (!(Number(form.laneWidth) >= 2)) return 'Inserisci una larghezza corsia valida.';
      if (!(Number(form.flow) >= 0) || form.flow === null || form.flow === '') {
        return 'Il flusso è obbligatorio per una manovra veicolare.';
      }
      if (!(Number(form.capSatResult) > 0)) {
        return form.isManual
          ? 'Inserisci una capacità di saturazione valida.'
          : 'Calcola la capacità di saturazione.';
      }
    }
    if (form.inputType === 'pedonale' && !(Number(form.verdeMin) > 0)) {
      return 'Il verde minimo è obbligatorio per una manovra pedonale.';
    }
    return '';
  };

  const submit = async () => {
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }
    setSaving(true);
    try {
      await onSave({
        title: form.title.trim() || defaultTitle,
        inputType: form.inputType,
        movementType: form.movementType,
        approachBearing: Number(form.approachBearing),
        lanes: Number(form.lanes),
        laneWidth: Number(form.laneWidth),
        color: form.color,
        capSatResult: form.inputType === 'veicolare' ? Number(form.capSatResult) : null,
        verdeMin: form.inputType === 'pedonale' ? Number(form.verdeMin) : 0,
        flussi: form.inputType === 'veicolare' ? [{ id: 1, value: String(form.flow) }] : [],
        inputSource: 'manual',
        isManual: form.isManual,
        capacityInputs: form.capacityInputs,
      });
    } catch (saveError) {
      setError(saveError.message || 'Salvataggio della manovra non riuscito.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Modal
        className="semafix-modal"
        open={open}
        title="Inserisci le informazioni"
        width={620}
        zIndex={MODAL_Z_INDEX}
        onCancel={onCancel}
        onOk={submit}
        okText="Salva manovra"
        cancelText="Annulla"
        confirmLoading={saving}
      >
        {geometry?.points > 0 && (
          <div className="semafix-modal-intro">
            <span>Geometria <b>{geometry.points} punti</b></span>
            <span>Lunghezza <b>{geometry.lengthMeters} m</b></span>
          </div>
        )}
        {error && <Alert className="semafix-editor-alert" type="error" showIcon message={error} />}

        <div className="semafix-editor-form-section">
          <p className="semafix-editor-form-legend">Manovra</p>
          <Row gutter={[14, 14]}>
            <Col span={24}>
              <label className="semafix-editor-field">
                <span>Nome manovra</span>
                <Input
                  value={form.title}
                  placeholder={defaultTitle}
                  onChange={(event) => update('title', event.target.value)}
                />
              </label>
            </Col>
            <Col span={24}>
              <Radio.Group
                value={form.inputType}
                onChange={(event) => update('inputType', event.target.value)}
                optionType="button"
                buttonStyle="solid"
                options={[
                  { label: 'Input veicolare', value: 'veicolare' },
                  { label: 'Input pedonale', value: 'pedonale' },
                ]}
              />
            </Col>
          </Row>
        </div>

        {form.inputType === 'veicolare' && (
          <div className="semafix-editor-form-section">
            <p className="semafix-editor-form-legend">Dati veicolari</p>
            <Row gutter={[14, 14]}>
              <Col xs={24} sm={12}>
                <label className="semafix-editor-field">
                  <span>Direzione di approccio</span>
                  <Select
                    value={form.approachBearing}
                    onChange={(value) => update('approachBearing', value)}
                    options={[
                      { label: 'Nord', value: 0 },
                      { label: 'Est', value: 90 },
                      { label: 'Sud', value: 180 },
                      { label: 'Ovest', value: 270 },
                    ]}
                  />
                </label>
              </Col>
              <Col xs={24} sm={12}>
                <label className="semafix-editor-field">
                  <span>Tipo di manovra</span>
                  <Select
                    value={form.movementType}
                    onChange={(value) => update('movementType', value)}
                    options={[
                      { label: 'Dritto', value: 'straight' },
                      { label: 'Svolta a destra', value: 'right' },
                      { label: 'Svolta a sinistra', value: 'left' },
                    ]}
                  />
                </label>
              </Col>
              <Col xs={24} sm={12}>
                <label className="semafix-editor-field">
                  <span>Numero corsie</span>
                  <InputNumber min={1} max={12} value={form.lanes} onChange={(value) => update('lanes', value)} />
                </label>
              </Col>
              <Col xs={24} sm={12}>
                <label className="semafix-editor-field">
                  <span>Larghezza corsia (m)</span>
                  <InputNumber min={2} max={8} step={0.05} value={form.laneWidth} onChange={(value) => update('laneWidth', value)} />
                </label>
              </Col>
              <Col xs={24} sm={12}>
                <label className="semafix-editor-field">
                  <span>Flusso (veicoli/ora)</span>
                  <InputNumber min={0} value={form.flow} onChange={(value) => update('flow', value)} />
                </label>
              </Col>
              <Col xs={24} sm={12}>
                <label className="semafix-editor-switch">
                  <span>Calcola CapSat manualmente</span>
                  <Switch checked={form.isManual} onChange={(checked) => update('isManual', checked)} />
                </label>
              </Col>
              <Col span={24}>
                {form.isManual ? (
                  <label className="semafix-editor-field">
                    <span>Capacità di saturazione (veicoli/ora)</span>
                    <InputNumber
                      min={1}
                      value={form.capSatResult}
                      onChange={(value) => update('capSatResult', value)}
                    />
                  </label>
                ) : (
                  <div className="semafix-editor-capacity-row">
                    <Button
                      type="primary"
                      icon={<CalculatorOutlined />}
                      onClick={() => setCapacityOpen(true)}
                    >
                      Calcola CapSat
                    </Button>
                    {Number(form.capSatResult) > 0
                      ? <b>Risultato CapSat: {Number(form.capSatResult).toLocaleString('it-IT')} veicoli/ora</b>
                      : <span>Nessun risultato ancora.</span>}
                  </div>
                )}
              </Col>
            </Row>
          </div>
        )}

        {form.inputType === 'pedonale' && (
          <div className="semafix-editor-form-section">
            <p className="semafix-editor-form-legend">Dati pedonali</p>
            <Row gutter={[14, 14]}>
              <Col xs={24} sm={12}>
                <label className="semafix-editor-field">
                  <span>Verde minimo (s)</span>
                  <InputNumber
                    min={1}
                    value={form.verdeMin}
                    onChange={(value) => update('verdeMin', value)}
                  />
                </label>
              </Col>
            </Row>
          </div>
        )}

        <div className="semafix-editor-form-section">
          <p className="semafix-editor-form-legend">Colore sulla mappa</p>
          <div className="semafix-editor-swatches">
            {PRESET_COLORS.map((color) => (
              <button
                key={color}
                type="button"
                aria-label={`Colore ${color}`}
                className={form.color.toUpperCase() === color ? 'selected' : ''}
                style={{ background: color }}
                onClick={() => update('color', color)}
              />
            ))}
            <input
              type="color"
              className="semafix-editor-color"
              aria-label="Colore personalizzato"
              value={form.color}
              onChange={(event) => update('color', event.target.value)}
            />
          </div>
        </div>
      </Modal>

      <CapacityCalculatorModal
        open={capacityOpen}
        onCancel={() => setCapacityOpen(false)}
        onCalculated={(value, inputs) => {
          setForm((current) => ({
            ...current,
            capSatResult: value,
            capacityInputs: inputs,
          }));
          setError('');
        }}
      />
    </>
  );
}
